import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltPageHeader } from '@cae/cobalt-react/components/page-header';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useHasPermission } from '@contexts/permissions-provider';
import { useUsersData } from '@features/users/contexts/data/useUsersData';
import { useUsersFilter } from '@features/users/contexts/filter/useUsersFilter';
import * as React from 'react';

import { useUserNavigation } from '../../hooks/use-user-navigation/useUserNavigation';
import { UsersFilterPanel } from '../filter-panel/UsersFilterPanel';
import { UsersFilterButton } from './filter-button/UsersFilterButton';
import { UsersSearch } from './search/UsersSearch';
import styles from './UsersHeader.module.css';

const UsersHeader = React.memo((): React.ReactElement => {
  const { users } = useUsersData();
  const { isFiltersApplied } = useUsersFilter();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false);
  const isMobileAndTablet = useMediaQuery(MediaQuery.MobileAndTablet);
  const canAdd = useHasPermission('Users_Add_Org');
  const { navigateToCreateUser } = useUserNavigation();

  const controlsDisabled =
    users.isLoading || (users.data.total === 0 && !isFiltersApplied);

  const handleFiltersOpen = React.useCallback((): void => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleFiltersClose = React.useCallback((): void => {
    setIsFilterPanelOpen(false);
  }, []);

  return (
    <section className={styles.usersHeader} data-testid="users-header">
      <CobaltPageHeader
        title="Users"
        className={styles.usersPageHeader}
        actions={
          !isMobileAndTablet &&
          canAdd && (
            <CobaltButton
              data-testid="create-user-button"
              onClick={navigateToCreateUser}
            >
              Create new user
            </CobaltButton>
          )
        }
        content={
          <div
            className={styles.usersHeaderToolbar}
            data-testid="users-header-toolbar"
          >
            <UsersSearch disabled={controlsDisabled} />

            <UsersFilterButton
              disabled={controlsDisabled}
              isLoading={users.isLoading}
              onFiltersOpen={handleFiltersOpen}
            />
          </div>
        }
      />

      {isFilterPanelOpen && <UsersFilterPanel onClose={handleFiltersClose} />}
    </section>
  );
});

UsersHeader.displayName = 'UsersHeader';

export { UsersHeader };
